/**
 * Authentication Context
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService, tokenStorage } from '../services';
import { getAuthErrorMessage } from '../utils/errorUtils';
import { User, AuthState } from '../types';
import { setCacheScope, clearAll as clearDataCache } from '../services/cache';

// ============================================
// Types
// ============================================

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<boolean>;
  refreshUser: () => Promise<void>;
}

// ============================================
// Context
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================
// Provider
// ============================================

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  // Initialize auth state. Use existing token first (e.g. after impersonation); only fall back to
  // refresh cookie when we have no token or the existing token is invalid. Otherwise restoreSession()
  // would overwrite an impersonation token with the cookie user (e.g. admin) and redirect back to admin.
  useEffect(() => {
    const initAuth = async () => {
      if (tokenStorage.hasTokens()) {
        try {
          const user = await authService.getCurrentUser();
          clearDataCache();
          setCacheScope({
            role: user.role,
            clientCode: user.clientCode,
            includeSharedData: user.includeSharedData,
          });
          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
          });
          return;
        } catch {
          // Token may be expired; try restore from httpOnly cookie
        }
      }
      const restored = await authService.restoreSession();
      if (restored || tokenStorage.hasTokens()) {
        try {
          const user = await authService.getCurrentUser();
          clearDataCache();
          setCacheScope({
            role: user.role,
            clientCode: user.clientCode,
            includeSharedData: user.includeSharedData,
          });
          setState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
          });
        } catch (err) {
          authService.clearTokens();
          setState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            error: null,
          });
        }
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, []);

  // Keep dashboard cache keys namespaced by client data scope
  useEffect(() => {
    if (state.user) {
      setCacheScope({
        role: state.user.role,
        clientCode: (state.user as any).clientCode,
        includeSharedData: (state.user as any).includeSharedData,
      });
    } else {
      setCacheScope(null);
    }
  }, [state.user]);

  // Listen for forced logout events (from token refresh failure)
  useEffect(() => {
    const handleLogout = () => {
      clearDataCache();
      setCacheScope(null);
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Login
  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const tokenResponse = await authService.login({ email, password });
      authService.setTokens(tokenResponse.accessToken, ''); // refresh token is in httpOnly cookie

      const user = await authService.getCurrentUser();
      clearDataCache();
      setCacheScope({
        role: user.role,
        clientCode: (user as any).clientCode,
        includeSharedData: (user as any).includeSharedData,
      });
      setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        error: null,
      });
      return true;
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, []);

  // Logout
  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearDataCache();
      setCacheScope(null);
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    }
  }, []);

  const refreshUser = useCallback(async (): Promise<void> => {
    if (!tokenStorage.hasTokens()) return;
    try {
      const user = await authService.getCurrentUser();
      clearDataCache();
      setCacheScope({
        role: user.role,
        clientCode: user.clientCode,
        includeSharedData: user.includeSharedData,
      });
      setState((prev) => (prev.isAuthenticated ? { ...prev, user } : prev));
    } catch (_) {}
  }, []);

  // Team/client scope can be changed by an administrator while this session is
  // open. Refresh it whenever the user returns to the app so scoped dashboard
  // caches are cleared and the new permissions take effect immediately.
  useEffect(() => {
    const refreshOnFocus = () => {
      void refreshUser();
    };
    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshUser();
      }
    };
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisibility);
    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
    };
  }, [refreshUser]);

  // Register
  const register = useCallback(async (
    email: string,
    password: string,
    name: string
  ): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const tokenResponse = await authService.register({ email, password, name });
      authService.setTokens(tokenResponse.accessToken, ''); // refresh token is in httpOnly cookie

      const user = await authService.getCurrentUser();
      clearDataCache();
      setCacheScope({
        role: user.role,
        clientCode: user.clientCode,
        includeSharedData: user.includeSharedData,
      });
      setState({
        isAuthenticated: true,
        isLoading: false,
        user,
        error: null,
      });
      return true;
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;